import _ from "lodash";
import io from "socket.io-client";
import { connect } from "react-redux";
import { createNotification } from "react-redux-notify";
import React, { createContext, useCallback, useRef, useState } from "react";
import { isMobile, isSafari, isAndroid, isFirefox, isChrome } from "react-device-detect";

import {
  setStreamerChannel,
  setStreamerChannelLoading,
  addLiveStreamingTipPayment,
  fetchStreamingChannelSuccess,
  appendStreamingChannelMessage,
  setLiveStreamingChannelMessages,
} from "../../store/actions/LiveStreamingAction";
import TokenStorage from "../storage/token";
import { StreamingEvents } from "../socket/events";
import PermissionService from '../services/streaming/permission';
import { getErrorNotificationMessage } from "../../components/helper/NotificationMessage";
import { getSuccessNotificationMessage } from "../../components/helper/NotificationMessage";
import { getUserId } from "../storage/user";
import { appConfig } from "../../config";

let socket;
const url = appConfig.streamingSocketHost;

export const SocketContext = createContext({});

function SocketProvider({ children, channel, ...props }) {
  const playerRef = useRef(null);
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [hasAllPermission, setHasAllPermission] = useState(true);
  const [isCallPermissionModalOpen, setIsCallPermissionModalOpen] = useState({
    isOpen: false,
    message: null,
  });
  const [isPermissionDeniedState, setIsPermissionDeniedState] = useState(false)

  const handlePermissionModal = (message) => {
    setIsCallPermissionModalOpen({
      isOpen: true,
      message: message,
    });
  };

  const connect = () => {
    socket = io(`${url}?token=${TokenStorage.getToken()}`, {
      secure: true,
      reconnection: true,
      transports: ["websocket"],
      timeout: 15000,
      pingTimeout: 15000,
      pingInterval: 45000,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      setConnected(true);
    });

    socket.on("error", () => {
      setConnected(false);
    });

    socket.on(StreamingEvents.START_STREAMING_RESPONSE, (payload) => {
      props.dispatch(setStreamerChannel(payload));
      props.dispatch(
        createNotification(getSuccessNotificationMessage("Streaming started"))
      );
      handleStartStreaming();
    });

    socket.on(StreamingEvents.STOP_STREAMING_RESPONSE, (payload) => {
      props.dispatch(setStreamerChannel(payload));
      props.dispatch(
        createNotification(getSuccessNotificationMessage("Streaming ended"))
      );
    });

    socket.on(StreamingEvents.SET_STREAMER_CHANNEL, (payload) => {
      props.dispatch(setStreamerChannel(payload));
    });

    socket.on(StreamingEvents.SET_STREAMING_CHANNEL, (payload) => {
      props.dispatch(fetchStreamingChannelSuccess(payload));
    });

    socket.on(StreamingEvents.STREAMING_CHANNEL_MESSAGES, (payload) => {
      props.dispatch(setLiveStreamingChannelMessages(payload));
    });

    socket.on(
      StreamingEvents.BROADCAST_STREAMING_CHANNEL_MESSAGE,
      (payload) => {
        props.dispatch(appendStreamingChannelMessage(payload));
      }
    );

    socket.on(
      StreamingEvents.STREAMING_TIP_BROADCAST_TIP_RECEIVED,
      (payload) => {
        props.dispatch(addLiveStreamingTipPayment(payload));
      }
    );

    socket.on(StreamingEvents.ERROR, (payload) => {
      const { error } = payload;
      if (error) {
        if (error?.payload?.error && _.isString(error.payload.error)) {
          props.dispatch(
            createNotification(getErrorNotificationMessage(error.payload.error))
          );
        } else {
          props.dispatch(
            createNotification(
              getErrorNotificationMessage("Something went wrong!")
            )
          );
        }
      }
    });
  };

  const sendTipSentNotification = (payload) => {
    socket.emit(StreamingEvents.STREAMING_TIP_SENT_NOTIFICATION, payload);
  };

  const sendTextMessage = (payload) => {
    socket.emit(StreamingEvents.SEND_STREAMING_CHANNEL_TEXT_MESSAGE, payload);
  };

  const streamBinaryData = (payload) => {
    socket.emit(StreamingEvents.SEND_STREAMING_DATA, payload);
  };

  const fetchStreamingChannelMessages = (payload) => {
    socket.emit(StreamingEvents.FETCH_STREAMING_CHANNEL_MESSAGES, payload);
  };

  const updateChannel = (channelId, userId, payload) => {
    socket.emit(StreamingEvents.UPDATE_CHANNEL, {
      channelId,
      userId,
      ...payload,
    });
  };

  const initializeChannel = (channelId) => {
    socket.emit(StreamingEvents.INITIALIZE_CHANNEL, { id: channelId });
  }

  const joinLiveStreamChannel = (channelId, userId) => {
    socket.emit(StreamingEvents.JOIN_CHANNEL, { channelId, userId });
  };

  const leaveLiveStreamChannel = (channelId, userId) => {
    socket.emit(StreamingEvents.LEAVE_CHANNEL, { channelId, userId });
  };

  const getUserLiveChannel = (userId) => {
    socket.emit(StreamingEvents.FETCH_USER_LIVE_CHANNEL, { userId });
  };

  const startChannelStreaming = (channelId) => {
    socket.emit(StreamingEvents.START_STREAMING, { channelId });
  };

  const stopChannelStreaming = (channelId) => {
    props.dispatch(setStreamerChannelLoading(true));
    socket.emit(StreamingEvents.STOP_STREAMING, { channelId });
  };

  const handleAudioConfiguration = async () => {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        suppressLocalAudioPlayback: true
      }
    });
  };

  const initializeMediaRecorder = (stream, options) => {
    mediaRecorderRef.current = new MediaRecorder(stream, options);

    if (webcamRef.current?.video) {
      webcamRef.current.video.muted = true;
      webcamRef.current.video.volume = 0;
    }

    mediaRecorderRef.current.addEventListener("dataavailable", ({ data }) => {
      if (data.size > 0) {
        streamBinaryData(data);
      }
    });
    mediaRecorderRef.current.start(250);
  };

  const handleStartStreaming = useCallback(() => {
    if (!webcamRef.current?.stream) {
      return;
    }

    let mimeType;
    if (isMobile) {
      if (isChrome && !isAndroid) {
        mimeType = "video/mp4"
      } else {
        mimeType = !isAndroid && (!isChrome || isSafari) ? "video/mp4" : "video/webm";
      }
    } else {
      mimeType = isSafari ? "video/mp4" : "video/webm"
    }

    const options = {
      mimeType,
      videoBitsPerSecond: 2000000,
      audioBitsPerSecond: 128000
    };

    const { stream } = webcamRef.current;
    if (!stream.getAudioTracks().length) {
      handleAudioConfiguration()
        .then(audioStream => {
          stream.addTrack(audioStream.getAudioTracks()[0]);
          initializeMediaRecorder(stream, options);
        })
        .catch(error => {
          initializeMediaRecorder(stream, options);
        });
    } else {
      initializeMediaRecorder(stream, options)
    }
  }, [webcamRef]);

  const checkAllPermission = async () => {
    const checkPermission = await PermissionService.handleCameraPermission(handlePermissionModal, setIsPermissionDeniedState);
    if (!checkPermission) {
      setHasAllPermission(false);
      return false;
    } else {
      setHasAllPermission(true);
      setIsCallPermissionModalOpen({
        isOpen: false,
        message: null,
      });
      return true;
    }
  };

  const checkIsStreamerUser = (channel) => {
    if (channel?.data?.userId === getUserId()) {
      return true
    } else {
      return false
    }
  }

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        playerRef,
        webcamRef,
        mediaRecorderRef,

        connect,
        setConnected,
        updateChannel,
        sendTextMessage,
        streamBinaryData,
        getUserLiveChannel,
        stopChannelStreaming,
        startChannelStreaming,
        joinLiveStreamChannel,
        leaveLiveStreamChannel,
        sendTipSentNotification,
        fetchStreamingChannelMessages,
        initializeChannel,
        checkAllPermission,
        isCallPermissionModalOpen,
        hasAllPermission,
        setIsCallPermissionModalOpen,
        isPermissionDeniedState,
        checkIsStreamerUser,
        handleAudioConfiguration,
        setIsPermissionDeniedState
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

const mapStateToPros = (state) => ({
  channel: state.liveStreaming.channel,
});

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(mapStateToPros, mapDispatchToProps)(SocketProvider);
